"use client";

import type { RefObject } from "react";

import { motion } from "motion/react";
import { useRef } from "react";
import { useResizeObserver } from "usehooks-ts";
import { cn } from "../../utils/classnames";

/** Pixels per second. Slow enough to read, fast enough to not look stalled. */
const SCROLL_SPEED = 40;

/**
 * Separation between the two copies. Must match the `gap-12` below: each pass
 * travels one copy plus this gap, so a mismatch leaves a visible seam.
 */
const COPY_GAP = 48;

/** Held at the start of every pass so the opening words stay readable. */
const PASS_DELAY_SECONDS = 1.5;

/**
 * Wide layouts and reduced-motion users wrap the text instead of scrolling it.
 * Both opt out through CSS alone, which leaves the measurement below reporting
 * no overflow — so the animation switches itself off without a media query
 * having to be restated in JavaScript.
 */
const CONTAINER_CLASS_NAME =
  "block overflow-hidden lg:overflow-visible motion-reduce:overflow-visible";

const TRACK_CLASS_NAME =
  "flex w-max gap-12 lg:w-auto lg:block motion-reduce:w-auto motion-reduce:block";

const COPY_CLASS_NAME =
  "shrink-0 whitespace-nowrap lg:whitespace-normal motion-reduce:whitespace-normal";

type AudioPlayerCardMarqueeTextProps = {
  text: string;
};

/**
 * One line of text that scrolls itself when it is wider than its column.
 *
 * The card's narrow layout cannot fit an episode title: wrapping pushes the
 * transport controls off the card, and clamping drops the end of the title
 * outright. Scrolling the overflow holds the card's height steady while still
 * exposing the whole title.
 *
 * The second copy is decorative — it exists to make the loop seamless, and is
 * hidden from assistive technology so the title is announced once.
 */
function AudioPlayerCardMarqueeText({ text }: AudioPlayerCardMarqueeTextProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const copyRef = useRef<HTMLSpanElement>(null);
  const { width: containerWidth = 0 } = useResizeObserver({
    ref: containerRef as RefObject<HTMLElement>,
  });
  const { width: copyWidth = 0 } = useResizeObserver({
    ref: copyRef as RefObject<HTMLElement>,
  });

  // The tolerance absorbs sub-pixel text measurement, which would otherwise
  // scroll text that already fits.
  const isScrolling = copyWidth > containerWidth + 1;
  const scrollDistance = copyWidth + COPY_GAP;

  return (
    <span className={CONTAINER_CLASS_NAME} ref={containerRef}>
      <motion.span
        animate={isScrolling ? { x: [0, -scrollDistance] } : { x: 0 }}
        className={TRACK_CLASS_NAME}
        transition={
          isScrolling
            ? {
                x: {
                  duration: scrollDistance / SCROLL_SPEED,
                  ease: "linear",
                  repeat: Number.POSITIVE_INFINITY,
                  repeatDelay: PASS_DELAY_SECONDS,
                  repeatType: "loop",
                },
              }
            : { duration: 0 }
        }
      >
        <span className={COPY_CLASS_NAME} ref={copyRef}>
          {text}
        </span>
        {isScrolling ? (
          <span
            aria-hidden="true"
            className={cn(COPY_CLASS_NAME, "motion-reduce:hidden lg:hidden")}
          >
            {text}
          </span>
        ) : null}
      </motion.span>
    </span>
  );
}

export { AudioPlayerCardMarqueeText };
